import type { sharedPreviewResolutionSchema } from "schemas/bedrockApi/sharedPreviews";
import type {
  SharedPreviewLink,
  SharedPreviewTarget,
} from "schemas/generated/sharedPreview";
import type { z } from "zod";
import type { RequestFn } from "./request";

import { BedrockConfigError } from "./errors";

export type SharedPreviewResolution = z.infer<
  typeof sharedPreviewResolutionSchema
>;

export function createSharedPreviewsClient({
  request,
  getAuthenticatedProjectId,
  getRoutingFields,
}: {
  request: RequestFn;
  getAuthenticatedProjectId: () => string | null;
  getRoutingFields: () => {
    devAlias: string | null;
    initialSnapshotRef: string | null;
  };
}) {
  const getProjectId = () => {
    const projectId = getAuthenticatedProjectId();
    if (projectId === null) {
      throw new BedrockConfigError({
        message: "Shared preview management requires an authenticated project",
      });
    }
    return projectId;
  };

  return {
    open: async (input: {
      shareId: string;
      signal: AbortSignal | null;
    }): Promise<SharedPreviewResolution> => {
      return await request<SharedPreviewResolution>({
        method: "POST",
        path: `/api/v1/shared-previews/${encodeURIComponent(input.shareId)}/open`,
        signal: input.signal ?? undefined,
      });
    },
    getStatus: async (input: {
      shareId: string;
      signal: AbortSignal | null;
    }): Promise<SharedPreviewResolution> => {
      return await request<SharedPreviewResolution>({
        method: "GET",
        path: `/api/v1/shared-previews/${encodeURIComponent(input.shareId)}/status`,
        signal: input.signal ?? undefined,
      });
    },
    getOrCreateActiveLink: async (input: {
      target: SharedPreviewTarget;
    }): Promise<SharedPreviewLink> => {
      return await request<SharedPreviewLink>({
        method: "POST",
        path: "/api/v1/shared-previews",
        body: {
          projectId: getProjectId(),
          ...getRoutingFields(),
          target: input.target,
        },
      });
    },
    revoke: async (input: { shareId: string }) => {
      await request<{ shareId: string }>({
        method: "DELETE",
        path: `/api/v1/shared-previews/${encodeURIComponent(input.shareId)}`,
      });
    },
  };
}

export type SharedPreviewsClient = ReturnType<
  typeof createSharedPreviewsClient
>;
